import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { routesConfig } from "@/router/routes";
import { RouteConfig } from "@/router/types";
import ProtectedRoute from "../components/ProtectedRoute";
import PublicRoute from "../components/PublicRoute";

const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      gap: 2,
    }}
  >
    <CircularProgress size={36} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<LoadingFallback />}>{element}</Suspense>;
}

function buildRouteObjects(configs: RouteConfig[]): RouteObject[] {
  return configs
    .map<RouteObject | null>((route) => {
      // Redirect routes
      if (route.redirectTo) {
        if (route.index) {
          return {
            index: true,
            element: <Navigate to={route.redirectTo} replace />,
          };
        }
        return {
          path: route.path,
          element: <Navigate to={route.redirectTo} replace />,
        };
      }

      // Layout + children (optionally protected)
      if (route.layout && route.children && route.children.length > 0) {
        const Layout = route.layout;
        const layoutElement =
          route.requiredRole || route.allowNonAdmin ? (
            <ProtectedRoute
              requiredRole={route.requiredRole}
              allowNonAdmin={route.allowNonAdmin}
            >
              <Layout />
            </ProtectedRoute>
          ) : (
            <Layout />
          );
        return {
          path: route.path,
          element: withSuspense(layoutElement),
          children: buildRouteObjects(route.children),
        };
      }

      // Children without layout
      if (route.children && route.children.length > 0) {
        return {
          path: route.path,
          children: buildRouteObjects(route.children),
        };
      }

      // Leaf component route
      if (route.component) {
        const Component = route.component;
        const componentElement = route.public ? (
          <PublicRoute>
            <Component />
          </PublicRoute>
        ) : (
          <Component />
        );
        if (route.index) {
          return {
            index: true,
            element: withSuspense(componentElement),
          };
        }
        return {
          path: route.path,
          element: withSuspense(componentElement),
        };
      }

      return null;
    })
    .filter(Boolean) as RouteObject[];
}

export const router = createBrowserRouter(buildRouteObjects(routesConfig));

export default router;
